import {Injectable, OnDestroy} from '@angular/core';
import {Offer} from '../../../../models/offer/offer.model';
import {OfferSectionInterface} from '../../../api/sections-api/offer-section.interface';
import {CalculationService} from '../../calculation.service';
import {CalculationStorageService} from '../../../../../shared/services/calculation-storage.service';
import {OfferService} from '../../offer.service';
import {NavigateManagerService} from '../../../navigate-manager.service';
import {ApplicationStateService} from '../../../../../shared/services/application-state.service';
import {BehaviorSubject, Subscription} from 'rxjs/Rx';
import {RegistrationService} from '../../registration.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {IframeCommunicationService} from '../../../../../shared/services/iframe-communication.service';
import {AppMode} from '../../../../../shared/models/app-mode.enum';
import {PrepareRegistrationService} from '../../prepare-registration.service';
import {FiltersService} from '../../../../../shared/services/filters.service';
import {MetricsService} from '../../../../../shared/services/metrics.service';
import {CustomMetricsName} from '../../../../../shared/components/models/metrics/custom-metrics-name.enum';
import {MetricsType} from '../../../../../shared/components/models/metrics/metrics-type.enum';
import {SortOrder} from '../../../../../shared/enums/sort-order.enum';
import {OfferCoverageDestinyCode} from '../../../../enums/offer/offer-coverage-destiny-code.enum';
import {OfferResponseStatusCode} from '../../../../enums/offer/offer-response-status-code.enum';
import {
  OfferUpdateDialogComponent
} from '../../../../../shared/components/dialogs/offer-update-dialog/offer-update-dialog.component';
import {MatDialog, MatDialogRef} from '@angular/material';
import {InsuranceCompanyService} from '../../../../../shared/services/insurance-company.service';
import {
  OfferUnavailableDialogComponent
} from '../../../../../shared/components/dialogs/offer-unavailable-dialog/offer-unavailable-dialog.component';
import {TestingPresetsService} from '../../../../../shared/services/debugging/testing-presets.service';
import {TestingPresetKey} from '../../../../enums/debugging/testing-preset-key.enum';
import {DeviceService} from '../../../../../shared/services/device.service';

@Injectable()
export class OfferSectionStateService implements OfferSectionInterface, OnDestroy {

  offersUpdate$: BehaviorSubject<void> = new BehaviorSubject(null);
  private _offers: any[] = [];
  private _offerSection: OfferSectionInterface;
  private initFilters: Map<string, object>;
  private offersChange: Subscription;
  private offerUpdateDialogRef: MatDialogRef<OfferUpdateDialogComponent>;
  private readonly GET_OFFER_TIMEOUT = 15;

  constructor(private calculation: CalculationService,
              private stateService: ApplicationStateService,
              private storage: CalculationStorageService,
              private offerService: OfferService,
              private metricsService: MetricsService,
              private filtersService: FiltersService,
              private iframeService: IframeCommunicationService,
              private registrationService: RegistrationService,
              private prepareRegistrationService: PrepareRegistrationService,
              private managerService: NavigateManagerService,
              private dialog: MatDialog,
              private insuranceCompanyService: InsuranceCompanyService) {
    this.offersChange = this.offerService.$offersChange.subscribe(_ => this.filterOffers());
    this.filtersService.filtersChangeEvent.subscribe(() => this.filterOffers());
  }

  set offerSection(value: OfferSectionInterface) {
    this._offerSection = value;
  }

  get offerSection(): OfferSectionInterface {
    return this._offerSection;
  }

  get offers(): any[] {
    return this._offers;
  }

  set offers(value: any[]) {
    this._offers = value;
  }

  get selectedOffer(): Offer {
    return this.stateService.selectedOffer;
  }

  get isWaitingForOfferMode(): boolean {
    return this.stateService.isWaitingForOfferMode;
  }

  get hasRegistrationState(): boolean {
    return isNotNullOrUndefined(this.registrationService.registrationState);
  }

  get dataFromSessionIncluded(): boolean {
    return this.registrationService.registrationState.dataFromSessionIncluded;
  }

  get selectedOfferStatusCode(): OfferResponseStatusCode {
    return this.stateService.state.selectedOfferStatusCode;
  }

  get shouldRejectGetOffer(): boolean {
    return TestingPresetsService.isPresetActive(TestingPresetKey.REJECT_GET_OFFER);
  }

  public ngOnDestroy(): void {
    if (isNotNullOrUndefined(this.offersChange)) {
      this.offersChange.unsubscribe();
    }
  }

  changeOffer(offer: Offer, isGetOffer?: boolean) {
    this.stateService.saveNecessaryBusinessParamsFromOffer(offer.id);
    this.saveSelectedOfferInState(offer);
    this.stateService.selectedOfferStatusCode = isGetOffer ? OfferResponseStatusCode.GET_OFFER : OfferResponseStatusCode.CALCULATE_OFFER;
    this.sendOfferMetrics(offer);
    this.registrationService.resetOfferRegistrationData(false, isGetOffer);
    if (this.hasRegistrationState && !this.dataFromSessionIncluded) { // include data from session only after first offer select
      this.registrationService.includeDataFromSession();
    }
    this.stateService.selectedOfferOriginalModifications = (offer && !!offer.modifications) ? offer.modifications : null;
    if (offer) {
      if (!isGetOffer) {
      this.prepareRegistrationService.sendPrepareRegistration(offer.id);
      }
      if (this.insuranceCompanyService.isGetOfferRequired(this.stateService.insuranceCompanyCode)) {
        if (!isGetOffer) {
          this.handleGetOffer();
          this.managerService.goNext(false);
        }
      } else {
        this.managerService.goNext();
      }
      this.iframeService.onStepChange();
    }
  }

  filtersFromFilterService(filters) {
    this.initFilters = filters;
  };

  filterOffers(): void {
    this._offers = this.filtersService.filterOffers(this.offerService.offers);
    this.offersUpdate$.next();
  }

  setAppMode(mode: AppMode): void {
    this.stateService.mode = mode;
  }

  sortOffersByPolicyAmount(offers: Offer[], sortOrder: SortOrder): Offer[] {
    switch (sortOrder) {
      case SortOrder.ASCENDING:
        return offers.sort((a: Offer, b: Offer) => Offer.getOfferLowestPrice(a) - Offer.getOfferLowestPrice(b));
      case SortOrder.DESCENDING:
        return offers.sort((a: Offer, b: Offer) => Offer.getOfferLowestPrice(b) - Offer.getOfferLowestPrice(a));
      default:
        return offers;
    }
  }

  private handleGetOffer(): void {
    this.openOfferUpdateDialog();

    const waitingForGetOfferTimeout = setTimeout(() => {
      const afterClosed$ = this.offerUpdateDialogRef.afterClosed().subscribe(() => {
        afterClosed$.unsubscribe();
        if (this.selectedOfferStatusCode === OfferResponseStatusCode.CALCULATE_OFFER) {
          this.openOfferUnavailableDialog();
        }
      });
      this.offerUpdateDialogRef.close();
      eventStreamSubscription.unsubscribe();
    }, this.GET_OFFER_TIMEOUT * 1000);

    let eventStreamSubscription = this.offerService.getOfferEvent.subscribe(
        (getOfferEvent: Offer) => {
          if (isNotNullOrUndefined(getOfferEvent) && !this.shouldRejectGetOffer) {
            clearTimeout(waitingForGetOfferTimeout);
            this.offerUpdateDialogRef.close();
            this.changeOffer(getOfferEvent, true);
            eventStreamSubscription.unsubscribe();
          }
        }
    );
  }

  private openOfferUpdateDialog(): void {
    setTimeout(() => {
      this.offerUpdateDialogRef = this.dialog.open(OfferUpdateDialogComponent, OfferUpdateDialogComponent.getConfigBuilder().buildConfiguration({
        canCloseOnNavigation: false
      }));
    }, 300);
  }

  private openOfferUnavailableDialog(): void {
    this.dialog.open(OfferUnavailableDialogComponent, OfferUnavailableDialogComponent.getConfigBuilder().buildConfiguration({
      canCloseOnNavigation: false
    }));
  }

  private sendOfferMetrics(offer: Offer): void {
    if (offer.modifications && offer.modifications.length > 0) {
      this.metricsService.sendSimpleMetric(CustomMetricsName.INITIAL_MODIFICATIONS, MetricsType.PROCESS, JSON.stringify(offer.modifications));
    }
  }

  private getCode(filterKey: string): OfferCoverageDestinyCode {
    return filterKey === 'START_FILTER' ? OfferCoverageDestinyCode.PRIMARY_BOX : OfferCoverageDestinyCode.DETAIL_BOX;
  }

  private filterByBox(offer, filters, code = OfferCoverageDestinyCode.DETAIL_BOX) {
    this.removeEmptyFilters(filters);

    let result = [];
    for (let filterCode in filters) {
      if (filterCode) {
        const coverage = this.getOfferCoverage(offer, filterCode);
        let destiny;
        if (coverage) {
          destiny = this.getDestinyFromOfferCoverage(coverage, code);
        }

        if (destiny) {
          result = this.getResult(code, result, destiny, filters, filterCode);
        }
      }
    }

    if (code === OfferCoverageDestinyCode.DETAIL_BOX) {
      return this.reduceBoolArrToBoolValue(result)
    }

    return this.handlePrimaryBoxResult(filters, result);
  }

  private handlePrimaryBoxResult(filters, result) {
    const filterValues = Object.values(filters);
    const positiveResultsLength = result.filter(value => value).length;
    const positiveFiltersLength = filterValues.filter((fil: string) => fil.endsWith('YES')).length;
    return positiveResultsLength === positiveFiltersLength;
  }

  private getResult(code, result, destiny, filters, filterCode): boolean[] {
    if (code === OfferCoverageDestinyCode.PRIMARY_BOX) {
      return [...result, destiny.selectedOption === filters[filterCode] && filters[filterCode].endsWith('YES') ];
    } else {
      return [...result, destiny.selectedOption === filters[filterCode]];
    }
  }

  private reduceBoolArrToBoolValue(arr: boolean[]): boolean {
    return arr.reduce((sum, next) => sum && next, true);
  }

  private removeEmptyFilters(filters) {
    for (let filter in filters) {
      if (!filters[filter]) {
        delete filters[filter];
      }
    }
  }

  private getOfferCoverage(offer, code) {
    return offer.coverages.filter(coverage => coverage.code === code)[0]
  }

  private getDestinyFromOfferCoverage(coverage, code) {
    const result = coverage.destinies.filter(destiny => destiny.code === code);
    return result.length === 1 ? result[0] : null;
  }

  private saveSelectedOfferInState(offer: Offer): void {
    this.stateService.selectedOffer = offer;
  }
}
